/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

import { newId } from '../../core/ids';
import { builtinEffectiveSkin, manifestEffectiveSkin, mergeSkinOverrides } from './resolve';
import { cloneDefaultSkinTokens } from './defaultSkin';
import { applyFonts, applySkinToDocument } from './runtime';
import {
  cacheSkin,
  cachedSkin,
  deletePersonalSkin,
  getPersonalSkin,
  listPersonalSkins,
  loadSelection,
  putPersonalSkin,
  saveSelection,
} from './storage';
import { downloadSkinAssets, fetchSkinCatalog, fetchSkinManifest, wallpaperFromAssets } from './skinApi';
import { exportFileName, exportPersonalSkin, parsePersonalSkinPackage } from './skinPackage';
import type {
  EffectiveSkin,
  PersonalSkin,
  SkinCatalog,
  SkinCatalogItem,
  SkinManifest,
  SkinOverrides,
  SkinSelection,
  SkinUpdateInfo,
} from './types';

interface SkinContextValue {
  skin: EffectiveSkin;
  catalog: SkinCatalog;
  personalSkins: PersonalSkin[];
  selection: SkinSelection | null;
  currentManifest: SkinManifest | null;
  busy: boolean;
  error: string | null;
  offline: boolean;
  updateInfo: SkinUpdateInfo | null;
  pendingDownload: string | null;
  refreshCatalog: () => Promise<void>;
  applyOfficial: (item: SkinCatalogItem, version?: number) => Promise<void>;
  applyPersonal: (id: string) => Promise<void>;
  updateOverrides: (patch: SkinOverrides) => void;
  resetOverrides: () => void;
  savePersonal: (name: string) => Promise<PersonalSkin>;
  renamePersonal: (id: string, name: string) => Promise<void>;
  duplicatePersonal: (id: string, name: string) => Promise<PersonalSkin>;
  removePersonal: (id: string) => Promise<void>;
  exportPersonal: (id: string) => Promise<void>;
  importPersonal: (bytes: Uint8Array) => Promise<PersonalSkin>;
}

const DEFAULT_CATALOG: SkinCatalog = { skins: [] };
const SkinContext = createContext<SkinContextValue | null>(null);
const FALLBACK_SKIN = builtinEffectiveSkin();
const FALLBACK_VALUE: SkinContextValue = {
  skin: FALLBACK_SKIN,
  catalog: DEFAULT_CATALOG,
  personalSkins: [],
  selection: null,
  currentManifest: null,
  busy: false,
  error: null,
  offline: true,
  updateInfo: null,
  pendingDownload: null,
  refreshCatalog: () => Promise.resolve(),
  applyOfficial: () => Promise.resolve(),
  applyPersonal: () => Promise.resolve(),
  updateOverrides: () => undefined,
  resetOverrides: () => undefined,
  savePersonal: () => Promise.reject(new Error('皮肤服务尚未初始化')),
  renamePersonal: () => Promise.resolve(),
  duplicatePersonal: () => Promise.reject(new Error('皮肤服务尚未初始化')),
  removePersonal: () => Promise.resolve(),
  exportPersonal: () => Promise.resolve(),
  importPersonal: () => Promise.reject(new Error('皮肤服务尚未初始化')),
};

function applyEffective(
  effective: EffectiveSkin,
  manifest: SkinManifest | null,
  assets: Record<string, string>,
): void {
  applySkinToDocument(effective);
  if (manifest) {
    applyFonts(
      effective.tokens,
      assets,
      manifest.assets.filter((asset) => asset.kind === 'font').map((asset) => ({ id: asset.id, slot: asset.slot, mime: asset.mime })),
    );
  }
}

function safeMessage(error: unknown): string {
  return error instanceof Error ? error.message : '皮肤服务暂不可用';
}

export function SkinProvider({ children }: { children: ReactNode }): ReactElement {
  const initial = builtinEffectiveSkin(loadSelection()?.overrides ?? {});
  const [skin, setSkin] = useState<EffectiveSkin>(initial);
  const [catalog, setCatalog] = useState<SkinCatalog>(DEFAULT_CATALOG);
  const [personalSkins, setPersonalSkins] = useState<PersonalSkin[]>([]);
  const [selection, setSelection] = useState<SkinSelection | null>(() => loadSelection());
  const [currentManifest, setCurrentManifest] = useState<SkinManifest | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const [pendingDownload, setPendingDownload] = useState<string | null>(null);
  const currentAssets = useRef<Record<string, string>>({});
  const booted = useRef(false);
  const selectionRevision = useRef(0);
  const selectionGeneration = useRef(0);
  const latestSelection = useRef(selection);

  const commit = useCallback((
    effective: EffectiveSkin,
    manifest: SkinManifest | null,
    assets: Record<string, string>,
    nextSelection: SkinSelection,
  ) => {
    latestSelection.current = nextSelection;
    currentAssets.current = assets;
    applyEffective(effective, manifest, assets);
    setSkin(effective);
    setCurrentManifest(manifest);
    setSelection(nextSelection);
    saveSelection(nextSelection);
  }, []);

  const loadOfficial = useCallback(async (
    skinId: string,
    version: number,
    overrides: SkinOverrides,
    allowNetwork: boolean,
    revision?: number,
    restoreGeneration?: number,
  ): Promise<boolean> => {
    const pendingOverrides = (): SkinOverrides | null => {
      if (restoreGeneration !== undefined) {
        const selected = latestSelection.current;
        return restoreGeneration === selectionGeneration.current
          && selected?.kind === 'official' && selected.id === skinId && selected.version === version
          ? selected.overrides
          : null;
      }
      return revision === undefined || revision === selectionRevision.current ? overrides : null;
    };
    const cacheKey = `official:${skinId}:${String(version)}`;
    const cached = await cachedSkin(cacheKey);
    if (cached) {
      const currentOverrides = pendingOverrides();
      if (currentOverrides === null) return true;
      commit(
        manifestEffectiveSkin(cached.manifest, cached.assets, currentOverrides, !allowNetwork),
        cached.manifest,
        cached.assets,
        { kind: 'official', id: skinId, version, overrides: currentOverrides },
      );
      setOffline(!allowNetwork);
      return true;
    }
    if (!allowNetwork) return false;
    const manifest = await fetchSkinManifest(skinId, version);
    const assets = await downloadSkinAssets(manifest.assets);
    await cacheSkin({ id: cacheKey, manifest, assets, cachedAt: Date.now() });
    const currentOverrides = pendingOverrides();
    if (currentOverrides === null) return true;
    commit(
      manifestEffectiveSkin(manifest, assets, currentOverrides, false),
      manifest,
      assets,
      { kind: 'official', id: skinId, version, overrides: currentOverrides },
    );
    setOffline(false);
    return true;
  }, [commit]);

  const applyPersonal = useCallback(async (id: string, initialRevision?: number): Promise<void> => {
    const revision = initialRevision ?? ++selectionRevision.current;
    if (initialRevision === undefined) selectionGeneration.current += 1;
    const personal = await getPersonalSkin(id);
    const selected = latestSelection.current;
    if (initialRevision !== undefined) {
      if (initialRevision !== selectionGeneration.current || selected?.kind !== 'personal' || selected.id !== id) return;
    } else if (revision !== selectionRevision.current) return;
    if (!personal) {
      setError('个人皮肤不存在');
      return;
    }
    const overrides = initialRevision !== undefined && selected?.kind === 'personal' && selected.id === id
      ? selected.overrides
      : personal.overrides;
    const effective = manifestEffectiveSkin(personal.base, personal.assets, overrides, true);
    effective.source = 'personal';
    effective.label = personal.name;
    commit(effective, personal.base, personal.assets, {
      kind: 'personal',
      id: personal.id,
      version: personal.base.version.version,
      overrides,
    });
    setOffline(true);
    setError(null);
  }, [commit]);

  const applyOfficial = useCallback(async (item: SkinCatalogItem, version = item.latestVersion, initialRevision?: number): Promise<void> => {
    const revision = initialRevision ?? ++selectionRevision.current;
    if (initialRevision === undefined) selectionGeneration.current += 1;
    if (revision !== selectionRevision.current) return;
    setBusy(true);
    setPendingDownload(item.name);
    setError(null);
    try {
      // Applying an official skin intentionally resets personal overrides. This is
      // the one-click "restore this skin exactly as published" path.
      const overrides: SkinOverrides = {};
      const ok = await loadOfficial(item.id, version, overrides, true, revision);
      if (!ok) throw new Error('无法加载皮肤');
    } catch (failure) {
      const cached = await cachedSkin(`official:${item.id}:${String(version)}`);
      if (revision !== selectionRevision.current) return;
      if (cached) {
        commit(
          manifestEffectiveSkin(cached.manifest, cached.assets, selection?.overrides ?? {}, true),
          cached.manifest,
          cached.assets,
          { kind: 'official', id: item.id, version, overrides: selection?.overrides ?? {} },
        );
        setOffline(true);
      } else {
        setError(safeMessage(failure));
      }
    } finally {
      setPendingDownload(null);
      setBusy(false);
    }
  }, [commit, loadOfficial, selection]);

  const refreshCatalog = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      const next = await fetchSkinCatalog();
      setCatalog(next);
      setOffline(false);
      setError(null);
    } catch (failure) {
      setOffline(true);
      setError(safeMessage(failure));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    const revision = selectionRevision.current;
    const generation = selectionGeneration.current;
    void (async () => {
      const people = await listPersonalSkins();
      setPersonalSkins(people);
      const stored = loadSelection();
      if (stored?.kind === 'personal') {
        await applyPersonal(stored.id, generation);
      } else if (stored?.kind === 'official' && stored.version !== undefined) {
        setBusy(true);
        try {
          const ok = await loadOfficial(stored.id, stored.version, stored.overrides, true, revision, generation);
          if (!ok && revision === selectionRevision.current) setSkin(builtinEffectiveSkin(stored.overrides));
        } catch {
          const ok = await loadOfficial(stored.id, stored.version, stored.overrides, false, revision, generation);
          if (!ok && revision === selectionRevision.current) setSkin(builtinEffectiveSkin(stored.overrides));
          setOffline(true);
        } finally {
          setBusy(false);
        }
      } else if (revision === selectionRevision.current) {
        applyEffective(initial, null, {});
      }
      try {
        const next = await fetchSkinCatalog();
        setCatalog(next);
        if (!stored && revision === selectionRevision.current) {
          const defaultSkin = next.skins.find((item) => item.isDefault) ?? next.skins[0];
          if (defaultSkin) await applyOfficial(defaultSkin, defaultSkin.latestVersion, revision);
        }
      } catch {
        setOffline(true);
      }
    })();
  }, [applyOfficial, applyPersonal, initial, loadOfficial]);

  const updateOverrides = useCallback((patch: SkinOverrides): void => {
    // A pending skin load must not overwrite edits made while it was downloading.
    selectionRevision.current += 1;
    const nextOverrides = { ...(selection?.overrides ?? {}), ...patch };
    const nextSelection: SkinSelection = selection === null
      ? { kind: 'official', id: 'builtin', overrides: nextOverrides }
      : { ...selection, overrides: nextOverrides };
    const nextSkin: EffectiveSkin = {
      ...skin,
      tokens: mergeSkinOverrides(skin.tokens, patch),
      wallpaperDataUrl: patch.wallpaperDataUrl === undefined ? skin.wallpaperDataUrl : patch.wallpaperDataUrl,
    };
    commit(nextSkin, currentManifest, currentAssets.current, nextSelection);
  }, [commit, currentManifest, selection, skin]);

  const resetOverrides = useCallback((): void => {
    if (!selection) return;
    if (selection.kind === 'personal') {
      void applyPersonal(selection.id);
      return;
    }
    selectionRevision.current += 1;
    selectionGeneration.current += 1;
    if (currentManifest) {
      const assets = currentAssets.current;
      commit(
        manifestEffectiveSkin(currentManifest, assets, {}, offline),
        currentManifest,
        assets,
        { ...selection, overrides: {} },
      );
    } else {
      commit(builtinEffectiveSkin(), null, {}, { kind: 'official', id: 'builtin', overrides: {} });
    }
  }, [applyPersonal, commit, currentManifest, offline, selection]);

  const savePersonal = useCallback(async (name: string): Promise<PersonalSkin> => {
    const cleanName = name.trim();
    if (cleanName === '') throw new Error('请输入个人皮肤名称');
    const base: SkinManifest = currentManifest ?? {
      skin: { id: 'builtin', slug: 'builtin', name: '内置基础皮肤', description: '', isDefault: false },
      version: { id: 'builtin-v1', version: 0, releaseNotes: '', publishedAt: null },
      tokens: cloneDefaultSkinTokens(),
      assets: [],
    };
    const now = Date.now();
    const personal: PersonalSkin = {
      id: newId(),
      name: cleanName,
      createdAt: now,
      updatedAt: now,
      base,
      assets: { ...currentAssets.current },
      overrides: { ...(selection?.overrides ?? {}) },
    };
    await putPersonalSkin(personal);
    setPersonalSkins(await listPersonalSkins());
    return personal;
  }, [currentManifest, selection]);

  const renamePersonal = useCallback(async (id: string, name: string): Promise<void> => {
    const personal = await getPersonalSkin(id);
    if (!personal) throw new Error('个人皮肤不存在');
    const clean = name.trim();
    if (clean === '') throw new Error('名称不能为空');
    await putPersonalSkin({ ...personal, name: clean, updatedAt: Date.now() });
    setPersonalSkins(await listPersonalSkins());
    if (selection?.kind === 'personal' && selection.id === id) {
      setSkin((current) => ({ ...current, label: clean }));
    }
  }, [selection]);

  const duplicatePersonal = useCallback(async (id: string, name: string): Promise<PersonalSkin> => {
    const personal = await getPersonalSkin(id);
    if (!personal) throw new Error('个人皮肤不存在');
    const now = Date.now();
    const copy: PersonalSkin = {
      ...personal,
      id: newId(),
      name: name.trim() || `${personal.name} 副本`,
      createdAt: now,
      updatedAt: now,
    };
    await putPersonalSkin(copy);
    setPersonalSkins(await listPersonalSkins());
    return copy;
  }, []);

  const removePersonal = useCallback(async (id: string): Promise<void> => {
    await deletePersonalSkin(id);
    setPersonalSkins(await listPersonalSkins());
    if (selection?.kind === 'personal' && selection.id === id) {
      const fallback = catalog.skins.find((item) => item.isDefault) ?? catalog.skins[0];
      if (fallback) await applyOfficial(fallback);
      else setSkin(builtinEffectiveSkin());
    }
  }, [applyOfficial, catalog.skins, selection]);

  const exportPersonal = useCallback(async (id: string): Promise<void> => {
    const personal = await getPersonalSkin(id);
    if (!personal) throw new Error('个人皮肤不存在');
    const blob = exportPersonalSkin(personal);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = exportFileName(personal);
    anchor.style.display = 'none';
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  }, []);

  const importPersonal = useCallback(async (bytes: Uint8Array): Promise<PersonalSkin> => {
    const imported = parsePersonalSkinPackage(bytes);
    const existing = await getPersonalSkin(imported.id);
    const personal: PersonalSkin = existing
      ? { ...imported, id: newId(), name: `${imported.name}（导入）`, createdAt: Date.now(), updatedAt: Date.now() }
      : { ...imported, updatedAt: Date.now() };
    await putPersonalSkin(personal);
    setPersonalSkins(await listPersonalSkins());
    return personal;
  }, []);

  const updateInfo = useMemo<SkinUpdateInfo | null>(() => {
    if (selection?.kind !== 'official' || selection.version === undefined) return null;
    const item = catalog.skins.find((entry) => entry.id === selection.id);
    return item && item.latestVersion > selection.version
      ? { latestVersion: item.latestVersion }
      : null;
  }, [catalog.skins, selection]);

  const value = useMemo<SkinContextValue>(() => ({
    skin, catalog, personalSkins, selection, currentManifest, busy, error, offline, updateInfo, pendingDownload,
    refreshCatalog, applyOfficial, applyPersonal, updateOverrides, resetOverrides,
    savePersonal, renamePersonal, duplicatePersonal, removePersonal,
    exportPersonal, importPersonal,
  }), [
    skin, catalog, personalSkins, selection, currentManifest, busy, error, offline, updateInfo, pendingDownload,
    refreshCatalog, applyOfficial, applyPersonal, updateOverrides, resetOverrides,
    savePersonal, renamePersonal, duplicatePersonal, removePersonal,
    exportPersonal, importPersonal,
  ]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const keep = new Set<string>();
    for (const [componentId, override] of Object.entries(skin.tokens.componentOverrides)) {
      if (override.text === undefined) continue;
      keep.add(componentId);
      document.querySelectorAll<HTMLElement>('[data-skin-editable-text="true"]')
        .forEach((element) => {
          if (element.dataset.skinId !== componentId) return;
          if (element.childElementCount > 0) return;
          element.dataset.skinOriginalText ??= element.textContent ?? '';
          if (element.textContent !== override.text) element.textContent = override.text ?? '';
          element.dataset.skinTextOverride = 'true';
        });
    }
    document.querySelectorAll<HTMLElement>('[data-skin-text-override="true"]').forEach((element) => {
      const id = element.dataset.skinId;
      if (id && keep.has(id)) return;
      if (element.dataset.skinOriginalText !== undefined) element.textContent = element.dataset.skinOriginalText;
      delete element.dataset.skinTextOverride;
    });
  }, [skin.tokens.componentOverrides, skin.label]);

  return <SkinContext.Provider value={value}>{children}</SkinContext.Provider>;
}

export function useSkin(): SkinContextValue {
  return useContext(SkinContext) ?? FALLBACK_VALUE;
}

export function useSkinCopy(key: string, fallback: string): string {
  const { skin } = useSkin();
  const override = skin.tokens.componentOverrides[key]?.text;
  return override ?? fallback;
}

export { wallpaperFromAssets };

